import { useState, useEffect, useCallback, useRef } from 'react';
import type { TenantConfig, UseTenantReturn } from '@/types/tenant';
import { companyService } from '@/services/company.service';
import { applyTenantTheme, getFallbackTenantConfig } from '@/lib/tenant';

const STORAGE_KEYS = {
  SELECTED_TENANT: 'inmocms_selected_tenant',
  TENANT_CONFIG: 'inmocms_tenant_config',
  LAST_FETCH: 'inmocms_tenant_last_fetch',
} as const;

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export function useTenant(): UseTenantReturn {
  const [config, setConfig] = useState<TenantConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  
  // Use refs to prevent unnecessary re-renders and function recreations
  const isInitialized = useRef(false);
  const isLoading = useRef(false);
  const currentConfigRef = useRef<TenantConfig | null>(null);
  
  // Update ref when config changes
  useEffect(() => {
    currentConfigRef.current = config;
  }, [config]);

  // Monitor online status - stable effect
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []); // Empty dependency array - this effect never changes

  /**
   * Load cached config from localStorage - stable function
   */
  const loadCachedConfig = useCallback((): TenantConfig | null => {
    try {
      const cachedConfig = localStorage.getItem(STORAGE_KEYS.TENANT_CONFIG);
      const lastFetch = localStorage.getItem(STORAGE_KEYS.LAST_FETCH);
      
      if (cachedConfig && lastFetch) {
        const cacheAge = Date.now() - parseInt(lastFetch);
        if (cacheAge < CACHE_DURATION) {
          return JSON.parse(cachedConfig);
        }
      }
    } catch (error) {
      console.warn('Failed to load cached tenant config:', error);
    }
    return null;
  }, []); // No dependencies - this is a pure function

  /**
   * Save config to localStorage - stable function
   */
  const saveConfigToCache = useCallback((tenantConfig: TenantConfig): void => {
    try {
      localStorage.setItem(STORAGE_KEYS.TENANT_CONFIG, JSON.stringify(tenantConfig));
      localStorage.setItem(STORAGE_KEYS.LAST_FETCH, Date.now().toString());
    } catch (error) {
      console.warn('Failed to cache tenant config:', error);
    }
  }, []); // No dependencies - this is a pure function

  /**
   * Load tenant configuration - stable function with useCallback
   */
  const loadTenantConfig = useCallback(async (forceRefresh = false): Promise<void> => {
    // Prevent multiple simultaneous calls
    if (isLoading.current && !forceRefresh) {
      return;
    }

    isLoading.current = true;
    setLoading(true);
    setError(null);

    try {
      // Try to get selected tenant from localStorage first
      const savedTenant = localStorage.getItem(STORAGE_KEYS.SELECTED_TENANT);
      
      // If not forcing refresh and offline, try to use cached config
      if (!forceRefresh && !isOnline) {
        const cachedConfig = loadCachedConfig();
        if (cachedConfig) {
          setConfig(cachedConfig);
          applyTenantTheme(cachedConfig);
          setError('Offline mode. Using cached configuration.');
          return;
        }
      }

      // If not forcing refresh, try cached config first
      if (!forceRefresh) {
        const cachedConfig = loadCachedConfig();
        if (cachedConfig) {
          setConfig(cachedConfig);
          applyTenantTheme(cachedConfig);
          // Continue to fetch fresh data in background if online
          if (!isOnline) {
            setError('Offline mode. Using cached configuration.');
            return;
          }
        }
      }

      // Determine which slug to use
      let slug = savedTenant;
      if (!slug) {
        // Get default slug from URL or environment
        slug = companyService.getDefaultCompanySlug();
      }

      // Fetch from API if online
      if (isOnline) {
        try {
          const tenantConfig = await companyService.getCompanyBySlug(slug);
          setConfig(tenantConfig);
          applyTenantTheme(tenantConfig);
          saveConfigToCache(tenantConfig);
          setError(null);
        } catch (apiError) {
          console.error('API fetch failed:', apiError);
          
          // If we don't have a cached config, use fallback
          if (!currentConfigRef.current) {
            const fallbackConfig = getFallbackTenantConfig(slug);
            setConfig(fallbackConfig);
            applyTenantTheme(fallbackConfig);
            setError('Using default configuration due to connection error.');
          } else {
            setError('Using cached configuration. Connection failed.');
          }
        }
      } else {
        // Offline mode - use fallback if no cached config
        if (!currentConfigRef.current) {
          const fallbackConfig = getFallbackTenantConfig(slug);
          setConfig(fallbackConfig);
          applyTenantTheme(fallbackConfig);
          setError('Offline mode. Using default configuration.');
        }
      }

    } catch (error) {
      console.error('Error loading tenant config:', error);
      
      // Use fallback configuration as last resort
      if (!currentConfigRef.current) {
        const fallbackConfig = getFallbackTenantConfig();
        setConfig(fallbackConfig);
        applyTenantTheme(fallbackConfig);
      }
      setError('Using default configuration due to error.');
    } finally {
      setLoading(false);
      isLoading.current = false;
      isInitialized.current = true;
    }
  }, [isOnline, loadCachedConfig, saveConfigToCache]); // Stable dependencies

  /**
   * Switch to a different tenant - stable function
   */
  const switchTenant = useCallback(async (slugOrId: string): Promise<void> => {
    if (!isOnline) {
      setError('Cannot switch tenant while offline');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const newConfig = await companyService.getCompanyBySlug(slugOrId);
      setConfig(newConfig);
      applyTenantTheme(newConfig);
      saveConfigToCache(newConfig);

      // Save the selected tenant
      localStorage.setItem(STORAGE_KEYS.SELECTED_TENANT, newConfig.slug);

    } catch (error) {
      console.error('Error switching tenant:', error);
      setError(error instanceof Error ? error.message : 'Failed to switch tenant');
    } finally {
      setLoading(false);
    }
  }, [isOnline, saveConfigToCache]); // Stable dependencies

  /**
   * Refetch current configuration - stable function
   */
  const refetch = useCallback(async (): Promise<void> => {
    await loadTenantConfig(true);
  }, [loadTenantConfig]); // Stable dependency

  // Initial load - only run once when component mounts
  useEffect(() => {
    if (!isInitialized.current && !isLoading.current) {
      loadTenantConfig();
    }
  }, []); // Empty dependency array to run only once

  // Refetch when coming back online (but only if we had an error and haven't initialized)
  useEffect(() => {
    if (isOnline && error && isInitialized.current && !isLoading.current) {
      // Debounce this to avoid rapid calls
      const timeoutId = setTimeout(() => {
        loadTenantConfig(true);
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
  }, [isOnline, error, loadTenantConfig]); // Only depend on these specific values

  return {
    config,
    loading,
    error,
    switchTenant,
    refetch,
    isOnline,
  };
}